import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Session } from '@supabase/supabase-js';
import type { Profile } from '../types/database';
import { supabase } from '../lib/supabase';

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isInitialized: boolean;
  
  // Actions
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setProfile: (profile: Profile | null) => void;
  setLoading: (loading: boolean) => void;
  initialize: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

// Track if we've started initialization (prevents double init)
let initializationStarted = false;
let initializationPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      profile: null,
      isLoading: true,
      isInitialized: false,

      setUser: (user) => set({ user }),
      setSession: (session) => set({ session }),
      setProfile: (profile) => set({ profile }),
      setLoading: (isLoading) => set({ isLoading }),

      initialize: async () => {
        // Prevent multiple initializations
        if (initializationStarted) {
          return initializationPromise || Promise.resolve();
        }
        
        initializationStarted = true;
        
        initializationPromise = (async () => {
          try {
            // Get current session from Supabase
            const { data: { session }, error } = await supabase.auth.getSession();
            
            if (error) {
              console.error('Auth session error:', error);
              set({ user: null, session: null, profile: null, isInitialized: true, isLoading: false });
              return;
            }
            
            if (session?.user) {
              set({ user: session.user, session });
              
              // Fetch fresh profile
              const { data: profile } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', session.user.id)
                .single();
              
              set({ profile, isInitialized: true, isLoading: false });
            } else {
              // No session - user is logged out
              set({ user: null, session: null, profile: null, isInitialized: true, isLoading: false });
            }
            
            // Listen for auth changes (login, logout, token refresh)
            supabase.auth.onAuthStateChange(async (event, newSession) => {
              if (newSession?.user) {
                set({ user: newSession.user, session: newSession });
                
                // Fetch profile on login
                if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                  const { data: profile } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', newSession.user.id)
                    .single();
                  set({ profile });
                }
              } else {
                set({ user: null, session: null, profile: null });
              }
            });
          } catch (error) {
            console.error('Auth initialization error:', error);
            set({ user: null, session: null, profile: null, isInitialized: true, isLoading: false });
          }
        })();
        
        return initializationPromise;
      },

      signOut: async () => {
        await supabase.auth.signOut();
        set({ user: null, session: null, profile: null });
        localStorage.removeItem('audc-auth');
      },

      refreshProfile: async () => {
        const { user } = get();
        if (!user) return;
        
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        set({ profile });
      },
    }),
    {
      name: 'audc-auth',
      // Only persist profile for instant loading
      partialize: (state) => ({ profile: state.profile }),
    }
  )
);

// CRITICAL: Start initialization immediately when module loads
// This ensures auth is ready as soon as possible
useAuthStore.getState().initialize();
